import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Check, X, Calendar, Phone, User, Package, ShoppingBag, Inbox } from "lucide-react";
import {
  getMarketplaceProfile,
  getStudioBookings,
  getMyBookings,
  updateStudioBooking,
  cancelBooking,
  MarketplaceBooking,
  BookingStatus,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { fadeUp, formatDate, isProfileCompleted } from "./MarketplaceShared";
import {
  BOOKING_STATUS_BADGE_STYLES,
  LISTING_TYPE_DISPLAY_LABELS,
  MARKETPLACE_GRADIENT,
} from "../../organisms/Dashboard/Marketplace/marketplaceConstants";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import MarketplaceLoader from "./MarketplaceLoader";

type ActiveTab = "received" | "mine";

const MarketplaceMyBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ActiveTab>("received");
  const [receivedBookings, setReceivedBookings] = useState<MarketplaceBooking[]>([]);
  const [myBookings, setMyBookings] = useState<MarketplaceBooking[]>([]);
  const [loadingReceived, setLoadingReceived] = useState(true);
  const [loadingMine, setLoadingMine] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  // Track whether the page has completed its first load — prevents the
  // full-screen video loader from re-appearing on action-triggered refetches.
  const initialized = useRef(false);

  
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/marketplace");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      }, 100);
      return;
    }
    getMarketplaceProfile()
      .then((res) => {
        const profileData = res?.data || null;
        if (!isProfileCompleted(profileData)) {
          navigate("/marketplace/profile/create");
        } else {
          localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
          setAuthChecking(false);
        }
      })
      .catch(() => {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const prof = parsed?.data || parsed;
            if (isProfileCompleted(prof)) {
              setAuthChecking(false);
              return;
            }
          } catch {}
        }
        navigate("/marketplace/profile/create");
      });
  }, []);

  const fetchReceived = async (silent = false) => {
    if (!silent) setLoadingReceived(true);
    try {
      const res = await getStudioBookings();
      setReceivedBookings(res.data ?? []);
    } catch {
      setReceivedBookings([]);
    } finally {
      if (!silent) setLoadingReceived(false);
    }
  };

  const fetchMine = async (silent = false) => {
    if (!silent) setLoadingMine(true);
    try {
      const res = await getMyBookings({ page_size: 50 });
      setMyBookings(res.results ?? []);
    } catch {
      setMyBookings([]);
    } finally {
      if (!silent) setLoadingMine(false);
    }
  };

  useEffect(() => {
    if (!authChecking) {
      // Initial page load — not silent, so loading flags are set
      Promise.all([fetchReceived(), fetchMine()]).finally(() => {
        initialized.current = true;
      });
    }
  }, [authChecking]);

  const handleAccept = async (id: string) => {
    setActionId(id);
    try {
      await updateStudioBooking(id, { status: "accepted" });
      sendFirebaseEvent('Marketplace_Booking_Accepted', 'Marketplace', 'booking_accepted', id);
      showSuccessToast("Booking accepted!");
      await fetchReceived(true); // silent — no full-screen loader
    } catch {
      showErrorToast("Failed to accept booking");
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionId(id);
    try {
      await updateStudioBooking(id, { status: "rejected" });
      sendFirebaseEvent('Marketplace_Booking_Rejected', 'Marketplace', 'booking_rejected', id);
      showSuccessToast("Booking rejected");
      await fetchReceived(true); // silent — no full-screen loader
    } catch {
      showErrorToast("Failed to reject booking");
    } finally {
      setActionId(null);
    }
  };

  const handleCancel = async (id: string) => {
    setActionId(id);
    try {
      await cancelBooking(id);
      sendFirebaseEvent('Marketplace_Booking_Cancelled', 'Marketplace', 'booking_cancelled', id);
      showSuccessToast("Inquiry cancelled");
      await fetchMine(true); // silent — no full-screen loader
    } catch {
      showErrorToast("Failed to cancel");
    } finally {
      setActionId(null);
    }
  };

  // Only show full-screen video on the very first page load, not on
  // action-triggered refetches (accept / reject / cancel).
  if (!initialized.current && (authChecking || loadingReceived || loadingMine)) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <PageLayout>
      <ContentWrap>
        {/* Header */}
        <PageHeader>
          <PageTitle>My Bookings & Inquiries</PageTitle>
          <PageSubtitle>Manage received inquiries on your listings and track inquiries you've sent</PageSubtitle>
        </PageHeader>

        {/* Tabs */}
        <TabBar>
          <Tab $active={tab === "received"} onClick={() => setTab("received")}>
            <Package size={15} />
            Received Inquiries
            <TabCount $active={tab === "received"}>{receivedBookings.length}</TabCount>
          </Tab>
          <Tab $active={tab === "mine"} onClick={() => setTab("mine")}>
            <ShoppingBag size={15} />
            My Inquiries
            <TabCount $active={tab === "mine"}>{myBookings.length}</TabCount>
          </Tab>
        </TabBar>

        {/* Received Inquiries */}
        {tab === "received" && (
          <>
            {receivedBookings.length === 0 ? (
              <EmptyState>
                <Inbox size={40} color="rgba(23,23,23,0.18)" />
                <EmptyTitle>No inquiries received yet</EmptyTitle>
                <EmptyText>
                  When buyers send inquiries on your listings, they'll appear here.
                </EmptyText>
                <OutlineBtn onClick={() => navigate("/marketplace/sell")}>
                  Add a Listing
                </OutlineBtn>
              </EmptyState>
            ) : (
              <BookingList>
                {receivedBookings.map((b) => {
                  const sc = BOOKING_STATUS_BADGE_STYLES[b.status] ?? BOOKING_STATUS_BADGE_STYLES.requested;
                  const ltLabel = LISTING_TYPE_DISPLAY_LABELS[b.listing_type] ?? b.listing_type;
                  return (
                    <BookingCard key={b.id}>
                      <BookingCardTop>
                        <TopLeft>
                          <ListingTypeBadge>{ltLabel}</ListingTypeBadge>
                          <BookingRef>#{b.reference}</BookingRef>
                        </TopLeft>
                        <StatusPill style={{ background: sc.bg, color: sc.color }}>
                          {sc.label}
                        </StatusPill>
                      </BookingCardTop>

                      <ListingTitle>{b.listing_title}</ListingTitle>

                      <BookingDetails>
                        {/* Customer info */}
                        <DetailRow>
                          <User size={14} color="#c94daa" />
                          <DetailLabel>Customer</DetailLabel>
                          <DetailVal>{b.customer_name || "—"}</DetailVal>
                        </DetailRow>
                        {b.customer_phone && (
                          <DetailRow>
                            <Phone size={14} color="#c94daa" />
                            <DetailLabel>Phone</DetailLabel>
                            <DetailVal>
                              {b.customer_country_code
                                ? `${b.customer_country_code} `
                                : ""}
                              {b.customer_phone}
                            </DetailVal>
                          </DetailRow>
                        )}
                        {(b.date_from || b.date_to) && (
                          <DetailRow>
                            <Calendar size={14} color="#c94daa" />
                            <DetailLabel>Dates</DetailLabel>
                            <DetailVal>
                              {formatDate(b.date_from) || "—"} →{" "}
                              {formatDate(b.date_to) || "—"}
                            </DetailVal>
                          </DetailRow>
                        )}
                        {b.amount_due && b.amount_due !== "0.00" && (
                          <DetailRow>
                            <DetailLabel>Amount</DetailLabel>
                            <DetailVal>₹{b.amount_due}</DetailVal>
                          </DetailRow>
                        )}
                        {b.customer_notes && (
                          <NotesBox>
                            <NotesLabel>Customer Notes</NotesLabel>
                            <NotesText>{b.customer_notes}</NotesText>
                          </NotesBox>
                        )}
                      </BookingDetails>

                      {b.status === "requested" && (
                        <BookingActions>
                          <AcceptBtn
                            onClick={() => handleAccept(b.id)}
                            disabled={actionId === b.id}
                          >
                            <Check size={14} />
                            {actionId === b.id ? "…" : "Accept"}
                          </AcceptBtn>
                          <RejectBtn
                            onClick={() => handleReject(b.id)}
                            disabled={actionId === b.id}
                          >
                            <X size={14} />
                            {actionId === b.id ? "…" : "Reject"}
                          </RejectBtn>
                        </BookingActions>
                      )}
                    </BookingCard>
                  );
                })}
              </BookingList>
            )}
          </>
        )}

        {/* My Inquiries */}
        {tab === "mine" && (
          <>
            {myBookings.length === 0 ? (
              <EmptyState>
                <ShoppingBag size={40} color="rgba(23,23,23,0.18)" />
                <EmptyTitle>No inquiries sent yet</EmptyTitle>
                <EmptyText>
                  Browse the marketplace and send inquiries on listings you're interested in.
                </EmptyText>
                <OutlineBtn onClick={() => navigate("/marketplace")}>
                  Browse Marketplace
                </OutlineBtn>
              </EmptyState>
            ) : (
              <BookingList>
                {myBookings.map((b) => {
                  const sc = BOOKING_STATUS_BADGE_STYLES[b.status] ?? BOOKING_STATUS_BADGE_STYLES.requested;
                  const ltLabel = LISTING_TYPE_DISPLAY_LABELS[b.listing_type] ?? b.listing_type;
                  return (
                    <BookingCard key={b.id}>
                      <BookingCardTop>
                        <TopLeft>
                          <ListingTypeBadge>{ltLabel}</ListingTypeBadge>
                          <BookingRef>#{b.reference}</BookingRef>
                        </TopLeft>
                        <StatusPill style={{ background: sc.bg, color: sc.color }}>
                          {sc.label}
                        </StatusPill>
                      </BookingCardTop>

                      <ListingTitle>{b.listing_title}</ListingTitle>

                      <BookingDetails>
                        <DetailRow>
                          <User size={14} color="#c94daa" />
                          <DetailLabel>Seller</DetailLabel>
                          <DetailVal>{b.studio_name || "—"}</DetailVal>
                        </DetailRow>
                        {(b.date_from || b.date_to) && (
                          <DetailRow>
                            <Calendar size={14} color="#c94daa" />
                            <DetailLabel>Dates</DetailLabel>
                            <DetailVal>
                              {formatDate(b.date_from) || "—"} →{" "}
                              {formatDate(b.date_to) || "—"}
                            </DetailVal>
                          </DetailRow>
                        )}
                        {b.amount_due && b.amount_due !== "0.00" && (
                          <DetailRow>
                            <DetailLabel>Amount</DetailLabel>
                            <DetailVal>₹{b.amount_due}</DetailVal>
                          </DetailRow>
                        )}
                        {b.customer_notes && (
                          <NotesBox>
                            <NotesLabel>Your Notes</NotesLabel>
                            <NotesText>{b.customer_notes}</NotesText>
                          </NotesBox>
                        )}
                      </BookingDetails>

                      {b.status === "requested" && (
                        <BookingActions>
                          <RejectBtn
                            onClick={() => handleCancel(b.id)}
                            disabled={actionId === b.id}
                          >
                            <X size={14} />
                            {actionId === b.id ? "…" : "Cancel Inquiry"}
                          </RejectBtn>
                        </BookingActions>
                      )}
                    </BookingCard>
                  );
                })}
              </BookingList>
            )}
          </>
        )}
      </ContentWrap>
    </PageLayout>
  );
};

export default MarketplaceMyBookingsPage;



const PageWrap = styled.div`
  min-height: 100vh;
  background: var(--mp-bodyBackground, #f7f8fc);
  font-family: Urbanist, sans-serif;
`;

const PageLayout = styled.div`
  min-height: calc(100vh - 64px);
  width: 100%;
  background: var(--mp-bodyBackground, #f7f8fc);
`;

const ContentWrap = styled.div`
  width: 100%;
  max-width: 1680px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeUp} 0.35s ease both;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const LoadingState = styled.div`
  text-align: center; padding: 80px 20px;
  font-family: Montserrat, sans-serif; font-size: 14px;
  color: var(--mp-subColor, rgba(23,23,23,0.4));
`;

const PageHeader = styled.div`display: flex; flex-direction: column; gap: 4px;`;

const PageTitle = styled.h1`
  margin: 0; font-size: 28px; font-weight: 800; color: var(--mp-textColor, #171717);
`;

const PageSubtitle = styled.p`
  margin: 0; font-family: Montserrat, sans-serif; font-size: 13px;
  color: var(--mp-subColor, rgba(23,23,23,0.5));
`;

const TabBar = styled.div`
  display: flex; gap: 0;
  background: var(--mp-cardBackground, white); border: 1px solid var(--mp-border, rgba(23,23,23,0.08));
  border-radius: 10px; overflow: hidden; width: fit-content;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex; align-items: center; gap: 7px;
  height: 44px; padding: 0 20px; border: none; cursor: pointer;
  font-family: Urbanist, sans-serif; font-size: 14px; font-weight: 700;
  background: ${(p) => p.$active ? "var(--mp-iconGrad, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%))" : "var(--mp-cardBackground, white)"};
  color: ${(p) => p.$active ? "white" : "var(--mp-subColor, rgba(23,23,23,0.6))"};
  transition: all 0.2s;
  &:hover { background: ${(p) => p.$active ? "var(--mp-iconGrad, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%))" : "var(--mp-bodyBackground, #f7f8fc)"}; }
`;

const TabCount = styled.span<{ $active: boolean }>`
  background: ${(p) => p.$active ? "rgba(255,255,255,0.25)" : "var(--mp-border, rgba(23,23,23,0.08))"};
  color: ${(p) => p.$active ? "white" : "var(--mp-subColor, rgba(23,23,23,0.55))"};
  padding: 2px 7px; border-radius: 20px; font-size: 11px; font-weight: 700;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; padding: 60px 24px;
  background: var(--mp-cardBackground, white); border-radius: 16px;
  border: 1px solid var(--mp-border, rgba(23,23,23,0.07));
`;

const EmptyTitle = styled.h3`
  margin: 0; font-size: 18px; font-weight: 800; color: var(--mp-subColor, rgba(23,23,23,0.5));
`;

const EmptyText = styled.p`
  margin: 0; font-family: Montserrat, sans-serif; font-size: 13px;
  color: var(--mp-subColor, rgba(23,23,23,0.4)); text-align: center; max-width: 340px;
`;

const OutlineBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  height: 38px; padding: 0 16px;
  border: 1.5px solid var(--mp-border, rgba(201,77,170,0.3)); border-radius: 9px;
  background: var(--mp-cardBackground, white); color: var(--mp-textColor, #171717);
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.03em;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: var(--mp-bodyBackground, #f7f8fc); border-color: var(--mp-textColor, #171717); }
`;

const BookingList = styled.div`display: flex; flex-direction: column; gap: 14px;`;

const BookingCard = styled.div`
  background: var(--mp-cardBackground, white); border: 1px solid var(--mp-border, rgba(23,23,23,0.07));
  border-radius: 12px; padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  display: flex; flex-direction: column; gap: 14px;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.1); }
`;

const BookingCardTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const TopLeft = styled.div`display: flex; align-items: center; gap: 10px;`;

const ListingTypeBadge = styled.span`
  font-size: 12px; font-weight: 700; padding: 4px 10px;
  border-radius: 20px; background: rgba(201,77,170,0.08);
  color: #c94daa; font-family: Montserrat, sans-serif;
`;

const BookingRef = styled.span`
  font-family: Montserrat, sans-serif; font-size: 11px;
  color: var(--mp-subColor, rgba(23,23,23,0.4)); font-weight: 700;
`;

const StatusPill = styled.span`
  font-size: 12px; font-weight: 700; padding: 5px 12px;
  border-radius: 20px; font-family: Montserrat, sans-serif;
`;

const ListingTitle = styled.h3`
  margin: 0; font-size: 17px; font-weight: 800; color: var(--mp-textColor, #171717);
`;

const BookingDetails = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  background: var(--mp-bodyBackground, #f9f9fb); border-radius: 12px; padding: 14px;
`;

const DetailRow = styled.div`
  display: flex; align-items: center; gap: 8px;
`;

const DetailLabel = styled.span`
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
  color: var(--mp-subColor, rgba(23,23,23,0.4)); text-transform: uppercase; letter-spacing: 0.04em;
  min-width: 64px;
`;

const DetailVal = styled.span`
  font-size: 14px; font-weight: 600; color: var(--mp-textColor, #171717);
`;

const NotesBox = styled.div`
  background: var(--mp-cardBackground, white); border-radius: 8px; padding: 10px 12px;
  border-left: 3px solid #c94daa; display: flex; flex-direction: column; gap: 4px;
  border-top: 1px solid var(--mp-border, transparent);
  border-right: 1px solid var(--mp-border, transparent);
  border-bottom: 1px solid var(--mp-border, transparent);
`;

const NotesLabel = styled.span`
  font-family: Montserrat, sans-serif; font-size: 10px; font-weight: 700;
  color: var(--mp-subColor, rgba(23,23,23,0.4)); text-transform: uppercase; letter-spacing: 0.06em;
`;

const NotesText = styled.p`
  margin: 0; font-size: 13px; color: var(--mp-subColor, rgba(23,23,23,0.7)); line-height: 1.5;
`;

const BookingActions = styled.div`
  display: flex; gap: 10px;
  @media (max-width: 480px) {
    flex-direction: column;
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const AcceptBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  height: 38px; padding: 0 18px; border: none;
  border-radius: 9px; background: rgba(34,197,94,1); color: white;
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.03em;
  cursor: pointer; transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(34,197,94,0.25);
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RejectBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  height: 38px; padding: 0 18px;
  border: 1.5px solid rgba(239,68,68,0.25); border-radius: 9px;
  background: rgba(239,68,68,0.06); color: #ef4444;
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.03em;
  cursor: pointer; transition: all 0.2s;
  &:hover:not(:disabled) { background: #ef4444; color: white; border-color: #ef4444; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
